import { z } from 'zod';

// Assignable sub-type catalog (GET document-subtypes) — the reclassify-menu source.
// Filter-independent reference data. Labels are backend-owned dynamic brain tags —
// they are NOT run through i18n.

// Tier 2 — Entity
export const DocumentSubtypeSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type DocumentSubtype = z.infer<typeof DocumentSubtypeSchema>;

// Tier 3 — Response. GET document-subtypes returns { subtypes: [...] } of assignable subtypes.
export const SubtypeCatalogResponseSchema = z.object({
  subtypes: z.array(DocumentSubtypeSchema),
});

// ── search_new `aggregations` block (spec da-19295 D3, routed-tab model) ──────────
// Fully backend-driven; mock-backed until the contract lands
// (see docs/DA-19430-search-new-contract.md).

// One document-type tab, exactly as the backend sends it. DA-19682 unified the tab shape with
// getWorkspaceCategories `tabs`: `id` is the routing category id (sent to search_new?category=<id>);
// `name_id` is the stable name ('all' | 'financial' | 'general'). Shared by the tile search_new
// block and grid documents/aggregations.
const DoctypeSchema = z.object({
  // backend may send numeric ids; URL segments are strings, so normalize to string
  id: z.union([z.string(), z.number()]).transform(String),
  name_id: z.string(),
  label: z.string(),
  count: z.number(),
});
export type SearchDoctype = z.infer<typeof DoctypeSchema>;

// Search facet chip (Inbox sub-type filter). Fully backend-driven; includes a leading
// "All" default chip with id:null — selected (highlighted) when no sub-type filter is active.
export const SearchSubtypeChipSchema = z.object({
  id: z.string().nullable(),
  label: z.string(),
});
export type SearchSubtypeChip = z.infer<typeof SearchSubtypeChipSchema>;

export const SearchAggregationsSchema = z.object({
  tabs: z.array(DoctypeSchema), // wire key is `tabs`; entries are the document-type tabs
  subtypes: z.array(SearchSubtypeChipSchema), // Inbox filter chips incl. the "All" default (id:null)
});
export type SearchAggregations = z.infer<typeof SearchAggregationsSchema>;

// ── documents/aggregations (spec da-19295 D9) ─────────────────────────────────
// View-agnostic categorization summary: doctype tabs + sub-type chips + active-category
// count under the GLOBAL filter (subtags[] only pins the active sub-type chip, does not narrow
// counts; no AG-Grid filterModel — the cascade).
// Same tab/chip entities as the tile search_new block (D3); adds a top-level `count`, and
// tabs/subtypes are null for tab-ineligible categories (base outside the Inbox subtree).
// Endpoint: GET /api/v2/{client}/documents/aggregations.

// Request query. looseObject (not deprecated .passthrough()) forwards the global filter
// already in the URL (date[]/invoice_date[]/payment_date[]/text[]/query/stag[]/isSigned/
// linkPanelID) untouched — mirrors grid-search minus filterModel; subtags[] is forwarded only to
// pin the active sub-type chip, not to narrow counts.
// companyId is NOT a query param: it's the client path / CompanyId header.
export const DocumentsAggregationsQuerySchema = z.looseObject({
  isConfidential: z.boolean(),
  category: z.string(),
  // DA-19691: the active sub-type. The backend PINS its chip into build_subtypes_response even at
  // zero results (guarantee-include, NOT a filter — count/tabs/sub-type buckets ignore it).
  subtags: z.array(z.string()).optional(),
});

export const DocumentsAggregationsResponseSchema = z.object({
  count: z.number(),
  tabs: z.array(DoctypeSchema).nullable(),
  subtypes: z.array(SearchSubtypeChipSchema).nullable(),
});
export type DocumentsAggregations = z.infer<typeof DocumentsAggregationsResponseSchema>;

// Publish task result (GET documents/{id}/accept/result — DA-19604 / DA-19619).
// Backend records the accept background-task result in Redis (1h TTL) in the SAME
// shape as the DOCUMENT_PUBLISH_COMPLETED WebSocket event, so a firewall-blocked
// client can poll for publish completion instead of relying on the socket.

// Tier 2 — Entity (one backend message)
export const AcceptResultMessageSchema = z.object({
  level: z.string(),
  type: z.string(),
  text: z.string(),
});
export type AcceptResultMessage = z.infer<typeof AcceptResultMessageSchema>;

// Tier 3 — Path + Response. A 404 (no result yet / expired) is handled by the caller,
// not modelled here; the 200 body carries the task status.
export const AcceptResultPathSchema = z.object({ documentID: z.string() });

export const AcceptResultResponseSchema = z.object({
  status: z.enum(['pending', 'success', 'failed']).nullable(),
  messages: z.array(AcceptResultMessageSchema).default([]),
  warning: z.string().optional(),
});
export type AcceptResultResponse = z.infer<typeof AcceptResultResponseSchema>;

// searchGetHints?prefix= — #-prefixed tag/user autocomplete hints. The backend returns an
// array of single-key objects `{ [hint]: { score } }`; the hint (user email / tag) is the
// object KEY (the { score } value is dropped downstream). `prefix` includes the leading `#`.
export const TagSuggestionsQuerySchema = z.object({ prefix: z.string() });

export const TagSuggestionsResponseSchema = z.array(z.record(z.string(), z.object({ score: z.number() })));
export type TagSuggestionsResponse = z.infer<typeof TagSuggestionsResponseSchema>;

// getNewSearchFavorites — sidebar FavoritesMenu source: system "query" favorites (ID:null) +
// user "saved" searches (UUID ID). Response is { items: [...] } (verified against live payload).
// `filter` is a save-request-only field (absent from reads); `new`/`total` are not sent — dropped
// (Zod strips unknown keys). Migrated from the legacy dashboard typed-contract validateSearchFavorites.
export const SearchFavoriteSchema = z.object({
  ID: z.string().nullish(), // null for system favorites, UUID for saved; legacy contract marked it optional
  label: z.string(),
  type: z.string(), // backend-driven ("query" | "saved"); consumed as `type === 'saved'` — kept string, not narrowed
  query: z.string().nullish(),
});
export type SearchFavorite = z.infer<typeof SearchFavoriteSchema>;

export const SearchFavoritesResponseSchema = z.object({
  items: z.array(SearchFavoriteSchema),
});
export type SearchFavoritesResponse = z.infer<typeof SearchFavoritesResponseSchema>;

// saveSearchCriteria body — label + backend filter string (built via makeSearchQuery) + the raw
// `documentType?query` string. deleteSearchCriteria body — the favorite ID.
export const SaveSearchCriteriaBodySchema = z.object({
  label: z.string(),
  filter: z.string(),
  query: z.string(),
});

export const DeleteSearchCriteriaBodySchema = z.object({
  ID: z.string(),
});

// Neither save nor delete responses are consumed (legacy refetched after save; delete removed by
// the ID it already held) — permissive to avoid false-positive api-zod-drift on a payload nothing reads.
export const SearchCriteriaMutationResponseSchema = z.unknown();

// getDashboardStats — document processing/count snapshot (fin/general per bucket). Consumed only by
// the Configurations → General fin-toggle guard (may the 'fin' feature be disabled?). Migrated from
// the legacy dashboard typed-contract validateDashboardState.
const DashboardStatItemSchema = z.object({ fin: z.number(), general: z.number() });
export const DashboardStatsResponseSchema = z.object({
  average: DashboardStatItemSchema,
  completed: DashboardStatItemSchema,
  ready: DashboardStatItemSchema,
  total: DashboardStatItemSchema,
  processing: z.number(),
});
export type DashboardStatsResponse = z.infer<typeof DashboardStatsResponseSchema>;

// ── document payments (staged in Dokka Pay) ──────────────────────────────────
// GET payments?documentID= lists the payments staged for one invoice; POST payments stages a
// new one. Both return the same payment entity — a large Pay-owned body (status, amounts,
// approvals, paymentInvoices…) of which the AP side consumes exactly one field: `id`, to
// deep-link the invoice page to the Pay payment form (DPM-656). Everything else rides through
// looseObject untouched. Asserting only what we read keeps a Pay-side field rename from
// breaking the invoice page over data it never looks at.
export const DocumentPaymentSchema = z.looseObject({
  id: z.string(),
});
export type DocumentPayment = z.infer<typeof DocumentPaymentSchema>;

export const DocumentPaymentsQuerySchema = z.object({ documentID: z.string() });

export const DocumentPaymentsResponseSchema = z.array(DocumentPaymentSchema);
export type DocumentPaymentsResponse = z.infer<typeof DocumentPaymentsResponseSchema>;

// POST payments/bulk — stage payments for the workspace bulk-panel selection.
export const BulkCreateDocumentPaymentsBodySchema = z.object({
  documentID: z.array(z.string()),
});

export const BulkCreateDocumentPaymentsResponseSchema = z.object({
  total: z.number(),
  success: z.number(),
  ignored: z.number(),
});
export type BulkCreateDocumentPaymentsResponse = z.infer<typeof BulkCreateDocumentPaymentsResponseSchema>;
